import React from "react";
import { favoriteLinksAdd, favoriteLinksRemove, linksDelete } from "../../../api/api";
import useApi from "../../../hooks/useApi";
import useRoutes from "../../../hooks/useRoutes";
import { useFavoriteLinks } from "../../../state/api/links/useFavoriteLinks";
import { useLinks } from "../../../state/api/links/useLinks";
import { Doc } from "../../../types/db/collections";
import { GetLinkResponse } from "../../../types/db/schema/links/link.schema";
import { AccordionPage, AccordionPageTitle } from "../accordion/AccordionPage";
import Button from "../buttons/Button";
import DeleteButton from "../buttons/DeleteButton";
import Pill from "../pill/Pill";
import LinkDialog from "./LinkDialog";
import "./UsefulLink.css";
import Icon from "../icons/Icon";
import { useSession } from "../../../state/api/session/useSession";
import AsAdmin from "../session/AsAdmin";
import { useQueryValue } from "../../../hooks/useSearch";
import { QUERY_KEY } from "../../../types/query.types";

interface Props {
  link: Doc<GetLinkResponse>,
  level?: number
}

export default function UsefulLink({ link, level = 0 }: Props) {

  const {
    openInBrowser
  } = useRoutes();

  const { mutate } = useLinks({ enabled: false });

  // const { value } = useQueryValue(QUERY_KEY.LINK_Q);

  const childCount = "children" in link && link.children?.length;


  const tags = link?.tags?.split(",").map(t => t.trim()).filter(t => !!t);

  const tagDisplay = (
    (!!tags?.length) && (
      <div className="d-flex flex-row flex-wrap gap-1">
        {
          tags.map(t => (
            <Pill
              size="small"
              background="var(--gray-100)"
              foreground="var(--gray-600)"
              key={t}
            >
              {t}
            </Pill>
          ))
        }
      </div>
    )
  );

  const header = (
    <div className="d-flex flex-column">
      <AccordionPageTitle>
        {link.target && (
          <Icon icon="link-45deg" className="me-1" />
        )}
        {link.label}
      </AccordionPageTitle>

    </div>
  )

  const description = (
    <div className="d-flex flex-column gap-1">
      <span className="font-s font-code" style={{ color: "var(--gray-400)" }}>{link.description}</span>
      {tagDisplay}
    </div>
  )

  if (childCount) {
    return (
      <AccordionPage
        // open={!!value}
        background="var(--teal-100)"
        hover="var(--gray-50)"
        title={(
          <div className="d-flex flex-row w-100 justify-content-between align-items-center gap-2" >
            {header}
            {
              !!childCount && (
                <Pill
                  round
                  background="var(--gray-100)"
                  foreground="var(--gray-600)"
                >
                  {childCount}
                </Pill>
              )
            }
          </div>
        )}
        subtitle={description}
        actionItems={(
          <ToggleFavoriteButton link={link} />
        )}
      >
        <div className="d-flex flex-column w-100 gap-1 ps-1 pe-1">
          {
            link.children?.map(child => (
              <UsefulLink
                link={child}
                key={child._id}
                level={level + 1}
              />
            ))
          }
        </div>
        <AsAdmin>
          <div className="ps-4 pe-4">
            <div className="d-flex flex-column">
              <strong className="text-uppercase font-s" style={{ color: "var(--gray-600)" }}>
                Actions
              </strong>
              <div className="d-flex flex-row gap-1">
                <LinkDialog parent={link} />
                <LinkDialog link={link} />
                <DeleteButton
                  apiCall={async () => await linksDelete(link._id)}
                  afterDelete={mutate}
                  size="small"
                />
              </div>
            </div>
          </div>
        </AsAdmin>
      </AccordionPage>
    )
  }

  const click = () => {
    if (!link.target) {
      return;
    }

    openInBrowser(link.target);
  }

  return (
    <div className="d-flex flex-column w-100 p-2 clickable-link gap-3" >
      <div className="w-100 d-flex flex-column">
        <div className="d-flex flex-row">
          <div
            className="w-100 d-flex flex-column"
            onClick={click}
          >
            {header}
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <ToggleFavoriteButton link={link} />
            <div className="d-flex flex-row gap-2 align-items-center">
              <LinkDialog parent={link} />
              <LinkDialog link={link} />
              <DeleteButton
                apiCall={async () => await linksDelete(link._id)}
                afterDelete={mutate}
                size="small"
              />
            </div>
          </div>
        </div>
        <div onClick={click}>
          {description}
        </div>
      </div>
      <div style={{ width: "100%", height: "1px", backgroundColor: "var(--gray-100)" }} />
    </div>
  );
}


function ToggleFavoriteButton({ link }: Props) {

  const { favoritesById, isLoading, mutate } = useFavoriteLinks();

  const callApi = useApi();

  const isFavorite = !!favoritesById?.[link._id];

  const clickHandler = async () => {
    await callApi(
      isFavorite
        ? favoriteLinksRemove(link._id)
        : favoriteLinksAdd(link._id)
    );

    await mutate();
  }

  if (!isFavorite && !link.target) {
    return null;
  }

  return (
    <Button
      size="small"
      variant="ghost"
      text={isFavorite ? "Favorite" : "Favorite"}
      icon={isFavorite ? "star-fill" : "star"}
      onClick={clickHandler}
      loading={isLoading}
    />
  );
}